import { defineStore } from 'pinia'

const CONSENT_COOKIE_NAME = 'consent_preferences'
const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year in seconds

function writeConsentCookie(preferences: CookiePreferences) {
  const value = JSON.stringify({
    analytics_storage: preferences.googleAnalytics ? 'granted' : 'denied',
    ad_storage: preferences.marketing ? 'granted' : 'denied',
    ad_user_data: preferences.marketing ? 'granted' : 'denied',
    ad_personalization: preferences.marketing ? 'granted' : 'denied',
  })
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Strict`
}

function deleteConsentCookie() {
  document.cookie = `${CONSENT_COOKIE_NAME}=; path=/; max-age=0; SameSite=Strict`
}

export interface CookiePreferences {
  necessary: boolean
  googleAnalytics: boolean
  googleMaps: boolean
  youtube: boolean
  microsoftBing: boolean
  googleTagManager: boolean
  marketing: boolean
}

export interface CookieConsentState {
  isVisible: boolean
  isAccepted: boolean
  preferences: CookiePreferences
  lastUpdated: string | null
}

export const useCookieConsentStore = defineStore('cookieConsent', {
  state: (): CookieConsentState => ({
    isVisible: false,
    isAccepted: false,
    preferences: {
      necessary: true, // Always true, cannot be disabled
      googleAnalytics: false,
      googleMaps: false,
      youtube: false,
      microsoftBing: false,
      googleTagManager: false,
      marketing: false
    },
    lastUpdated: null
  }),

  getters: {
    hasConsent: (state) => state.isAccepted,
    hasAnalyticsConsent: (state) => state.preferences.googleAnalytics,
    hasMarketingConsent: (state) => state.preferences.marketing,
    hasPreferencesConsent: (state) => state.preferences.youtube,
    shouldShowBanner: (state) => !state.isAccepted && !state.isVisible,
    canLoadTrackingScripts: (state) => state.isAccepted && (
      state.preferences.googleAnalytics || 
      state.preferences.googleMaps || 
      state.preferences.youtube
    )
  },

  actions: {
    initialize() {
      if (process.client) {
        const stored = localStorage.getItem('cookie-consent')
        if (stored) {
          try {
            const data = JSON.parse(stored)
            this.isAccepted = data.isAccepted || false
            this.preferences = { ...this.preferences, ...data.preferences }
            this.lastUpdated = data.lastUpdated || null
          } catch (error) {
          }
        }
      }
    },

    showBanner() {
      if (!this.isAccepted) {
        this.isVisible = true
      }
    },

    hideBanner() {
      this.isVisible = false
    },

    acceptAll() {
      this.preferences = {
        necessary: true,
        googleAnalytics: true,
        googleMaps: true,
        youtube: true,
        microsoftBing: true,
        googleTagManager: true,
        marketing: true
      }
      this.accept()
    },

    acceptOnlyNecessary() {
      this.preferences = {
        necessary: true,
        googleAnalytics: false,
        googleMaps: false,
        youtube: false,
        microsoftBing: false,
        googleTagManager: false,
        marketing: false
      }
      this.accept()
    },

    acceptSelected() {
      this.accept()
    },

    rejectAll() {
      this.preferences = {
        necessary: true,
        googleAnalytics: false,
        googleMaps: false,
        youtube: false,
        microsoftBing: false,
        googleTagManager: false,
        marketing: false
      }
      this.accept()
    },

    updatePreferences(preferences: Partial<CookiePreferences>) {
      this.preferences = { ...this.preferences, ...preferences }
    },

    accept() {
      this.isAccepted = true
      this.isVisible = false
      this.lastUpdated = new Date().toISOString()
      
      if (process.client) {
        localStorage.setItem('cookie-consent', JSON.stringify({
          isAccepted: this.isAccepted,
          preferences: this.preferences,
          lastUpdated: this.lastUpdated
        }))
        writeConsentCookie(this.preferences)
      }
    },

    reset() {
      this.isAccepted = false
      this.isVisible = false
      this.preferences = {
        necessary: true,
        googleAnalytics: false,
        googleMaps: false,
        youtube: false,
        microsoftBing: false,
        googleTagManager: false,
        marketing: false
      }
      this.lastUpdated = null
      
      if (process.client) {
        localStorage.removeItem('cookie-consent')
        deleteConsentCookie()
      }
    }
  }
}) 